import { DestroyRef, ElementRef, Injector, Renderer2, signal } from '@angular/core';
import { AppConsts } from '@shared/AppConsts';
import { AbpMultiTenancyService, FeatureCheckerService, LocalizationService, MessageService, NotifyService, PermissionCheckerService, SettingService } from 'abp-ng2-module';

import { AppSessionService } from '@shared/session/app-session.service';
import { Title } from '@angular/platform-browser';
import { Location } from '@angular/common';
import { PermissionConsts } from '@shared/permission-consts';
import { MessageService as PrimeMessageService, PrimeNGConfig } from 'primeng/api';
import { PrimengIT } from '../assets/primeng-locale/primeng-IT';
import { loadMessages, locale } from 'devextreme/localization';
import { DeviceDetectorService } from 'ngx-device-detector';
// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-call,@typescript-eslint/no-var-requires
const itMessages = require('devextreme/localization/messages/it.json');

export const topCenterToastKey = 'TOP_CENTER_MSG';
export const topRightToastKey = 'TOP_RIGHT_MSG';

export enum ToastPosition {
    topCenter = topCenterToastKey,
    topRight = topRightToastKey,
}

/**
 * @class AppComponentBase
 * @classdesc Abstract component class for all other application components. Implements various utility services and methods.
 */
export abstract class AppComponentBase {
    localizationSourceName = AppConsts.localization.defaultLocalizationSourceName;

    localization: LocalizationService;
    permission: PermissionCheckerService;
    feature: FeatureCheckerService;
    notify: NotifyService;
    setting: SettingService;
    message: MessageService;
    primeMessage: PrimeMessageService;
    primeConfig: PrimeNGConfig;
    multiTenancy: AbpMultiTenancyService;
    appSession: AppSessionService;
    elementRef: ElementRef;
    titlePage: Title;
    renderer2: Renderer2;
    deviceDetector: DeviceDetectorService;
    location: Location;
    destroyRef: DestroyRef;
    isMobile = signal<boolean>(false);

    constructor(injector: Injector) {
        this.localization = injector.get(LocalizationService);
        this.permission = injector.get(PermissionCheckerService);
        this.feature = injector.get(FeatureCheckerService);
        this.notify = injector.get(NotifyService);
        this.setting = injector.get(SettingService);
        this.message = injector.get(MessageService);
        this.primeMessage = injector.get(PrimeMessageService);
        this.primeConfig = injector.get(PrimeNGConfig);
        this.multiTenancy = injector.get(AbpMultiTenancyService);
        this.appSession = injector.get(AppSessionService);
        this.elementRef = injector.get(ElementRef);
        this.titlePage = injector.get(Title);
        this.renderer2 = injector.get(Renderer2);
        this.deviceDetector = injector.get(DeviceDetectorService);
        this.location = injector.get(Location);
        this.destroyRef = injector.get(DestroyRef);

        this.isMobile.set(this.deviceDetector.isMobile());

        this.primeConfig.setTranslation(PrimengIT);
        loadMessages(itMessages);
        // Il bundle it.json di DevExtreme (23.2.4) lascia questi due titoli non tradotti anche nella traduzione
        // ufficiale: li sovrascriviamo con i testi gestiti in Terapistiaba.xml.
        loadMessages({
            it: {
                'dxScheduler-confirmRecurrenceDeleteTitle': this.l('DeleteRecurringAppointmentTitle'),
                'dxScheduler-confirmRecurrenceEditTitle': this.l('EditRecurringAppointmentTitle'),
            },
        });
        // La lingua del browser puo' non coincidere con quella scelta nell'app (es. browser en-US, app in it):
        // DevExtreme deve seguire abp.localization.currentLanguage, non navigator.language, altrimenti i
        // messaggi di sistema (es. "Delete Recurring Appointment") restano nella lingua di default (en).
        locale(abp.localization.currentLanguage.name);
    }

    /**
     * Localize a given key string with its value on Localization Manager
     * @param key
     * @param args
     */
    l(key: string, ...args: any[]): string {
        let localizedText = this.localization.localize(key, this.localizationSourceName);

        if (!localizedText) {
            localizedText = key;
        }

        if (!args || !args.length) {
            return localizedText;
        }

        args.unshift(localizedText);
        return abp.utils.formatString.apply(this, args);
    }

    /**
     * Checks if the current user has access to a given permission
     * @param permissionName
     */
    isGranted(permissionName: string): boolean {
        return this.permission.isGranted(permissionName);
    }

    getUserSession() {
        return this.appSession.user;
    }

    /**
     * Permissions enum
     */
    get permissionsConst() {
        return PermissionConsts;
    }

    setTitle(t: string) {
        this.titlePage.setTitle(this.l(t));
    }

    startLoading() {
        abp.ui.setBusy(document.body);
        this.renderer2.addClass(document.body, 'overflow-hidden');
    }

    stopLoading() {
        abp.ui.clearBusy(document.body);
        this.renderer2.removeClass(document.body, 'overflow-hidden');
    }

    goBack() {
        this.location.back();
    }

    /**
     * Shows success toast with given message content
     * @param msg
     * @param position
     */
    successMessage(msg: string, position = ToastPosition.topCenter): void {
        this.primeMessage.add({ severity: 'success', summary: this.l('Success'), detail: msg, key: position.toString() });
    }

    /**
     * Shows info toast with given message content
     * @param msg
     * @param position
     */
    infoMessage(msg: string, position = ToastPosition.topCenter): void {
        this.primeMessage.add({ severity: 'info', summary: this.l('Info'), detail: msg, key: position.toString() });
    }

    /**
     * Shows warning toast with given message content
     * @param msg
     * @param position
     */
    warningMessage(msg: string, position = ToastPosition.topCenter): void {
        this.primeMessage.add({ severity: 'warn', summary: this.l('Warning'), detail: msg, key: position.toString() });
    }

    /**
     * Shows error toast with given message content
     * @param msg
     * @param position
     */
    errorMessage(msg: string, position = ToastPosition.topCenter): void {
        this.primeMessage.add({ severity: 'error', summary: this.l('Error'), detail: msg, key: position.toString() });
    }

    clearMessages(): void {
        this.primeMessage.clear();
    }

    isInRole(role: string): boolean {
        return this.appSession.user.roles.some((r) => r === role);
    }
}
